/* eslint-disable no-undef */
import type { AxiosRequestConfig, AxiosResponse } from "axios";
import axios from "axios";
import type {
  AdaLoginDto,
  AdaLoginResponseDto,
  CreateFeedbackDto,
  IndigenousCommunityStatusDto,
  ProductCategoryDto,
  PromoCodeDto,
} from "./types";
import {
  type AddressLookupDetailsDto,
  type AddressLookupDto,
  type AddressLookupQueryDto,
  type OrderCreatedDto,
  type OrderValidateDto,
  type GetProductCategoriesDto,
  type OrderContactDto,
  type EmergentVerifyEmailDto,
  type IndigenousCommunityProvinceListDto,
} from "./types";
import { buildQueryString } from "@/helpers/query";
import type {
  OrderDetails,
  OrderExtraCosts,
  OrderShippingDetails,
} from "@/store/types/order";
import type { RegisterDetailsModel } from "@/store/types/register";
import { appEnv } from "@/constants/env";
import { telemetry } from "@/telemetry";
import { trackApiError } from "./trackApiError";

type PathParams = Record<string, string | number>;

export interface CallApiOptions<TReturnData, TData>
  extends Omit<AxiosRequestConfig<TData>, "url" | "baseURL"> {
  endpoint: string;
  pathParams?: PathParams;
  transform?: (data: TReturnData) => TReturnData;
}

const buildPath = (endpoint: string, pathParams: PathParams = {}) =>
  endpoint.replace(/:(\w+)/g, (_, key: string) =>
    encodeURIComponent(pathParams[key]),
  );

export const callRawApi = async <TReturnData = unknown, TData = unknown>({
  endpoint,
  pathParams,
  transform,
  ...config
}: CallApiOptions<TReturnData, TData>) => {
  const method = (config.method ?? "GET").toUpperCase();
  const elapsed = telemetry.startTimer();
  const response = await axios
    .request<TReturnData, AxiosResponse<TReturnData>, TData>({
      ...config,
      url: `${appEnv.apiUrl}${buildPath(endpoint, pathParams)}`,
    })
    .catch((error) => {
      trackApiError(error, { method, endpoint, durationMs: elapsed() });
      throw error;
    });

  telemetry.trackDependency({
    method,
    endpoint,
    status: response.status,
    durationMs: elapsed(),
  });

  if (response.data && transform) {
    response.data = transform(response.data);
  }

  return response;
};

export const callApi = async <TReturnData = unknown, TData = unknown>(
  options: CallApiOptions<TReturnData, TData>,
) => {
  const response = await callRawApi<TReturnData, TData>(options);
  return response.data;
};

export const api = {
  getAddressLookups: (query: AddressLookupQueryDto) =>
    callApi<AddressLookupDto[]>({
      method: "GET",
      endpoint: "/address-lookups",
      params: query,
      paramsSerializer: { serialize: buildQueryString },
    }),

  getAddressLookupDetails: (addressId: string) =>
    callApi<AddressLookupDetailsDto>({
      method: "GET",
      endpoint: "/address-lookups/:addressId",
      pathParams: { addressId },
    }),

  getProductCategories: (data: GetProductCategoriesDto) => {
    return callApi<ProductCategoryDto[]>({
      method: "POST",
      endpoint: "/products",
      data,
    });
  },

  getExtraCosts: (data: OrderShippingDetails) => {
    return callApi<OrderExtraCosts>({
      method: "POST",
      endpoint: "/extra-costs",
      data,
    });
  },

  getOrganizationTypes: () =>
    callApi<string[]>({
      method: "GET",
      endpoint: "/orders/organization-types",
    }),

  createOrder: (data: OrderDetails) =>
    callApi<OrderCreatedDto>({
      method: "POST",
      endpoint: "/orders",
      data,
    }),

  verifyEmail: (data: EmergentVerifyEmailDto) =>
    callApi<unknown>({
      method: "POST",
      data,
      endpoint: "/verification-codes/get-code",
    }),

  verifyCode: ({
    code,
    captchaToken,
  }: {
    code: string;
    captchaToken: string;
  }) =>
    callApi<OrderContactDto>({
      method: "POST",
      data: {
        code,
        captchaToken,
      },
      endpoint: "/verification-codes/get-contact",
    }),

  createRegistration: (data: RegisterDetailsModel) =>
    callApi({
      method: "POST",
      endpoint: "/business-registrations",
      data,
    }),

  validateOrder: (orderId: string) =>
    callApi<OrderValidateDto>({
      method: "GET",
      endpoint: "/orders/validate-order/:orderId",
      pathParams: { orderId },
    }),

  getIndigenousCommunities: () => {
    return callApi<IndigenousCommunityProvinceListDto[]>({
      method: "GET",
      endpoint: "/indigenous-communities/simple",
    });
  },

  getIndigenousCommunityStatus: (id: number) => {
    return callApi<IndigenousCommunityStatusDto>({
      method: "GET",
      endpoint: "/indigenous-communities/:id/status",
      pathParams: { id },
    });
  },

  adaLogin: (data: AdaLoginDto) => {
    return callApi<AdaLoginResponseDto>({
      method: "POST",
      endpoint: "/logins/ada",
      data,
    });
  },

  getPromoCode: (code: string) => {
    return callApi<PromoCodeDto>({
      method: "GET",
      endpoint: "/promotional-codes/:code",
      pathParams: { code },
    });
  },

  createFeedback: (data: CreateFeedbackDto) => {
    return callApi({
      method: "POST",
      endpoint: "/feedback",
      data,
    });
  },
};
